"use client";

import { useRef, type ReactNode } from "react";
import { motion, useInView } from "framer-motion";
import { MapPin, Star, BadgeCheck } from "lucide-react";

/**
 * Showcase-blok op de landing: laat het monteurprofiel zien zoals het
 * verschijnt wanneer je op een stip op de dekkingskaart klikt — met
 * profielfoto, totale matchingsscore (>90%) en de score per criterium.
 * Statische mockup (geen API); kleuren via de huis-gradiënt rood→oranje→groen.
 */

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

// Zelfde gradiënt-logica als de kaart-popup: rood → oranje → groen.
function scoreToColor(score: number): string {
  const s = Math.max(0, Math.min(100, score));
  if (s >= 50) {
    const t = (s - 50) / 50;
    return `rgb(${Math.round(lerp(0xe3, 0x14, t))},${Math.round(lerp(0x85, 0xc0, t))},${Math.round(lerp(0x2a, 0x7a, t))})`;
  }
  const t = s / 50;
  return `rgb(${Math.round(lerp(0xe7, 0xe3, t))},${Math.round(lerp(0x4c, 0x85, t))},${Math.round(lerp(0x3c, 0x2a, t))})`;
}

const SCORE = 92;

const fmt = (n: number) => n.toLocaleString("nl-NL");
const Hl = ({ children }: { children: ReactNode }) => (
  <span className="font-semibold text-[var(--brand)]">{children}</span>
);

const ROWS: { label: string; val: number; max: number; detail: ReactNode }[] = [
  { label: "Beschikbaarheid", val: 30, max: 35, detail: "6 van 7 dagen · vandaag vrij" },
  {
    label: "Soort apparaat",
    val: 25,
    max: 25,
    detail: (
      <>
        Wasmachine · <Hl>Droger</Hl> · Vaatwasser
      </>
    ),
  },
  {
    label: "Merk & cert.",
    val: 20,
    max: 20,
    detail: (
      <>
        <Hl>Bosch ✓</Hl> · Siemens ✓ · AEG
      </>
    ),
  },
  { label: "First Time Fix", val: 12.5, max: 15, detail: "FTF-score 92%" },
  { label: "Reviews", val: 4.5, max: 5, detail: "4,5 van 5 · 18 reviews" },
];

export default function MatchShowcase() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="mx-auto w-full max-w-5xl px-5 py-14 sm:px-6 sm:py-24">
      <p className="mb-3 text-center font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)] sm:text-[11px]">
        De juiste match
      </p>
      <h2 className="mb-8 text-center text-[clamp(24px,4vw,36px)] font-semibold leading-tight tracking-[-0.025em] text-[var(--ink)] sm:mb-14">
        Match altijd de juiste monteur
      </h2>

      <div className="grid items-center gap-8 lg:grid-cols-[1fr_minmax(0,320px)] lg:gap-12">
        {/* Uitleg */}
        <div>
          <p className="max-w-md text-[14px] leading-[1.65] text-[var(--ink-2)] sm:text-[15px] sm:leading-[1.7]">
            Elke actieve monteur staat op de kaart. Klik op een stip en je ziet
            direct het volledige profiel: de totale matchingsscore en hoe hij
            scoort op beschikbaarheid, soort apparaat, merkkennis, First Time Fix
            en reviews.
          </p>
          <ul className="mt-5 flex flex-col gap-2.5 sm:mt-6 sm:gap-3">
            {[
              "Transparante score per criterium",
              "Alleen geverifieerde, actieve monteurs",
              "In één oogopslag de beste match boven 90%",
            ].map((t) => (
              <li key={t} className="flex items-center gap-2.5 text-[14px] text-[var(--ink)]">
                <BadgeCheck size={17} strokeWidth={1.75} className="shrink-0 text-[var(--brand)]" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        {/* Profielkaart — zoals de kaart-popup */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-[320px]"
        >
          {/* Map-achtige achtergrond met stip */}
          <div className="absolute -inset-4 -z-10 rounded-[28px] bg-[var(--surface-raised)] sm:-inset-6 [background-image:linear-gradient(var(--line)_1px,transparent_1px),linear-gradient(90deg,var(--line)_1px,transparent_1px)] [background-size:22px_22px] opacity-60" />
          <div className="absolute -top-2 left-1/2 -z-10 flex -translate-x-1/2 flex-col items-center">
            <span className="h-3 w-3 rounded-full bg-[var(--brand)] ring-4 ring-[color-mix(in_srgb,var(--brand)_25%,transparent)]" />
          </div>

          <div className="rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-5 shadow-[0_10px_36px_-16px_rgba(11,14,12,0.14)]">
            {/* Kop: foto + naam */}
            <div className="flex items-center gap-3">
              <img
                src="/images/monteur-portret.jpg"
                alt="Profielfoto monteur"
                className="h-12 w-12 shrink-0 rounded-full border-2 border-white object-cover shadow-sm"
              />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="truncate text-[15px] font-semibold text-[var(--ink)]">Arjan Mols</p>
                  <BadgeCheck size={15} strokeWidth={2} className="shrink-0 text-[var(--brand)]" />
                </div>
                <p className="flex items-center gap-1 text-[12px] text-[var(--ink-3)]">
                  <MapPin size={12} strokeWidth={1.75} />
                  Utrecht · 18 km
                </p>
              </div>
              <span className="ml-auto shrink-0 rounded-full bg-[color-mix(in_srgb,var(--brand)_14%,transparent)] px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--brand)]">
                Actief
              </span>
            </div>

            {/* Totale score */}
            <div className="mt-4 flex items-baseline justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Matchingsscore
              </span>
              <span className="text-[22px] font-extrabold tabular-nums text-[var(--brand)]">{SCORE}%</span>
            </div>
            <div className="relative mt-2 h-1.5 w-full overflow-hidden rounded-full">
              <div className="absolute inset-0 rounded-full bg-[linear-gradient(to_right,#E74C3C_0%,#E3852A_50%,#14C07A_100%)]" />
              <motion.div
                initial={{ width: "100%" }}
                animate={inView ? { width: `${100 - SCORE}%` } : {}}
                transition={{ duration: 0.9, ease: "easeOut" }}
                className="absolute inset-y-0 right-0 rounded-r-full bg-[var(--surface)]"
              />
            </div>

            {/* Breakdown per criterium */}
            <div className="mt-5 flex flex-col gap-3">
              {ROWS.map((r, i) => {
                const pct = Math.round((r.val / r.max) * 100);
                const color = scoreToColor(pct);
                return (
                  <div key={r.label}>
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-[var(--ink-2)]">{r.label}</span>
                      <span className="font-mono tabular-nums text-[var(--ink-3)]">
                        {fmt(r.val)}/{fmt(r.max)}
                      </span>
                    </div>
                    <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-[var(--surface-sunk)]">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={inView ? { width: `${pct}%` } : {}}
                        transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 + i * 0.08 }}
                        className="h-full rounded-full"
                        style={{ backgroundColor: color }}
                      />
                    </div>
                    <p className="mt-1 text-[10px] leading-snug text-[var(--ink-3)]">{r.detail}</p>
                  </div>
                );
              })}
            </div>

            {/* Voet: tarief + rating */}
            <div className="mt-4 flex items-center justify-between border-t border-[var(--line)] pt-3">
              <span className="text-[12px] font-medium text-[var(--ink)]">€ 105 / klus</span>
              <span className="flex items-center gap-1 text-[12px] text-[var(--ink-2)]">
                <Star size={13} strokeWidth={1.75} className="fill-[#E3852A] text-[#E3852A]" />
                4,8
              </span>
            </div>
            <div className="mt-2 flex items-center gap-1.5 text-[12px] text-[var(--ink-2)]">
              <BadgeCheck size={13} strokeWidth={1.75} className="text-[var(--brand)]" />
              <span className="font-semibold tabular-nums text-[var(--ink)]">{fmt(1453)}</span>
              uitgevoerde jobs
            </div>

            {/* Match-actie */}
            <button
              type="button"
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--brand)] px-5 py-2.5 text-[13px] font-medium text-[#0B0E0C] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
            >
              <BadgeCheck size={15} strokeWidth={1.75} />
              Match job
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
